'use client';

import React, { useState, useMemo } from 'react';
import AuditTrailFiltersBar, { AuditFilters, ActionType } from './AuditTrailFiltersBar';
import {
  PackageSearch, ShieldCheck, ShieldX, MapPin, ClipboardList,
  Layers, TruckIcon, PlusCircle, ChevronLeft, ChevronRight
} from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

interface AuditLog {
  id: string;
  timestamp: string;
  operator: string;
  device: string;
  action: ActionType;
  item: string;
  qty: string;
  location: string;
  grnId?: string;
  batchNo?: string;
}

// ─── Mock Data ────────────────────────────────────────────────────────────────

const MOCK_LOGS: AuditLog[] = [
  { id: 'AL-0001', timestamp: '2026-06-12T09:02:14', operator: 'Prasun Sharma', device: 'HH-01', action: 'GRN_CREATED', item: '—', qty: '—', location: 'Receiving Dock A', grnId: 'GRN-20260612-001' },
  { id: 'AL-0002', timestamp: '2026-06-12T09:08:33', operator: 'Prasun Sharma', device: 'HH-01', action: 'ITEM_ADDED', item: 'Non Woven Tape 75mm x 250cm', qty: '100 ROLL', location: 'Receiving Dock A', grnId: 'GRN-20260612-001', batchNo: 'BAT-2026-001' },
  { id: 'AL-0003', timestamp: '2026-06-12T09-11:05', operator: 'Prasun Sharma', device: 'HH-01', action: 'ITEM_ADDED', item: 'Sterile Gloves Size M', qty: '500 PCS', location: 'Receiving Dock A', grnId: 'GRN-20260612-001', batchNo: 'BAT-2026-002' },
  { id: 'AL-0004', timestamp: '2026-06-12T09:45:22', operator: 'Anita Desai', device: 'TAB-02', action: 'QC_APPROVED', item: 'Non Woven Tape 75mm x 250cm', qty: '100 ROLL', location: 'QC Station 1', grnId: 'GRN-20260612-001', batchNo: 'BAT-2026-001' },
  { id: 'AL-0005', timestamp: '2026-06-12T09:47:11', operator: 'Anita Desai', device: 'TAB-02', action: 'QC_REJECTED', item: 'Sterile Gloves Size M', qty: '500 PCS', location: 'QC Station 1', grnId: 'GRN-20260612-001', batchNo: 'BAT-2026-002' },
  { id: 'AL-0006', timestamp: '2026-06-12T10:05:44', operator: 'Ravi Kumar', device: 'HH-03', action: 'RACK_SLOTTED', item: 'Non Woven Tape 75mm x 250cm', qty: '100 ROLL', location: 'R-A-01-03', batchNo: 'BAT-2026-001' },
  { id: 'AL-0007', timestamp: '2026-06-12T10:22:09', operator: 'Suresh Patel', device: 'HH-02', action: 'ITEM_PICKED', item: 'Non Woven Tape 75mm x 250cm', qty: '20 ROLL', location: 'R-A-01-03', batchNo: 'BAT-2026-001' },
  { id: 'AL-0008', timestamp: '2026-06-12T10:35:58', operator: 'Suresh Patel', device: 'HH-02', action: 'KIT_COMPLETED', item: 'Surgical Kit #KIT-0042', qty: '1 KIT', location: 'Kitting Station 2' },
  { id: 'AL-0009', timestamp: '2026-06-12T11:00:00', operator: 'Meena Joshi', device: 'HH-04', action: 'GRN_CREATED', item: '—', qty: '—', location: 'Receiving Dock B', grnId: 'GRN-20260612-002' },
  { id: 'AL-0010', timestamp: '2026-06-12T11:08:17', operator: 'Meena Joshi', device: 'HH-04', action: 'ITEM_ADDED', item: 'Mayo S Trolley Cover Small', qty: '200 PCS', location: 'Receiving Dock B', grnId: 'GRN-20260612-002', batchNo: 'BAT-2026-003' },
  { id: 'AL-0011', timestamp: '2026-06-12T11:30:42', operator: 'Anita Desai', device: 'TAB-02', action: 'QC_APPROVED', item: 'Mayo S Trolley Cover Small', qty: '200 PCS', location: 'QC Station 1', batchNo: 'BAT-2026-003' },
  { id: 'AL-0012', timestamp: '2026-06-12T11:55:19', operator: 'Ravi Kumar', device: 'HH-03', action: 'RACK_SLOTTED', item: 'Mayo S Trolley Cover Small', qty: '200 PCS', location: 'R-B-02-01', batchNo: 'BAT-2026-003' },
  { id: 'AL-0013', timestamp: '2026-06-12T12:10:05', operator: 'Suresh Patel', device: 'HH-02', action: 'ITEM_PICKED', item: 'Mayo S Trolley Cover Small', qty: '50 PCS', location: 'R-B-02-01', batchNo: 'BAT-2026-003' },
  { id: 'AL-0014', timestamp: '2026-06-12T12:40:33', operator: 'Prasun Sharma', device: 'HH-01', action: 'DISPATCH', item: 'Surgical Kit #KIT-0042', qty: '1 KIT', location: 'Dispatch Bay 1' },
  { id: 'AL-0015', timestamp: '2026-06-12T13:05:11', operator: 'Ravi Kumar', device: 'HH-03', action: 'ITEM_PICKED', item: 'Non Woven Tape 75mm x 250cm', qty: '30 ROLL', location: 'R-A-01-03', batchNo: 'BAT-2026-001' },
  { id: 'AL-0016', timestamp: '2026-06-12T13:22:48', operator: 'Suresh Patel', device: 'HH-02', action: 'KIT_COMPLETED', item: 'Dressing Kit #KIT-0043', qty: '1 KIT', location: 'Kitting Station 1' },
  { id: 'AL-0017', timestamp: '2026-06-12T13:50:27', operator: 'Meena Joshi', device: 'HH-04', action: 'DISPATCH', item: 'Dressing Kit #KIT-0043', qty: '1 KIT', location: 'Dispatch Bay 2' },
  { id: 'AL-0018', timestamp: '2026-06-11T08:15:00', operator: 'Prasun Sharma', device: 'HH-01', action: 'GRN_CREATED', item: '—', qty: '—', location: 'Receiving Dock A', grnId: 'GRN-20260611-001' },
  { id: 'AL-0019', timestamp: '2026-06-11T08:30:22', operator: 'Prasun Sharma', device: 'HH-01', action: 'ITEM_ADDED', item: 'Surgical Drape Large', qty: '150 PCS', location: 'Receiving Dock A', grnId: 'GRN-20260611-001', batchNo: 'BAT-2026-010' },
  { id: 'AL-0020', timestamp: '2026-06-11T09:00:00', operator: 'Anita Desai', device: 'TAB-02', action: 'QC_APPROVED', item: 'Surgical Drape Large', qty: '150 PCS', location: 'QC Station 1', batchNo: 'BAT-2026-010' },
];

// ─── Action Config ─────────────────────────────────────────────────────────────

const ACTION_CONFIG: Record<ActionType | string, { label: string; color: string; bg: string; Icon: React.ComponentType<{ size?: number; className?: string }> }> = {
  GRN_CREATED:  { label: 'GRN Created',   color: 'text-blue-400',   bg: 'bg-blue-500/10',   Icon: PackageSearch },
  ITEM_ADDED:   { label: 'Item Added',    color: 'text-violet-400', bg: 'bg-violet-500/10', Icon: PlusCircle },
  QC_APPROVED:  { label: 'QC Approved',   color: 'text-green-400',  bg: 'bg-green-500/10',  Icon: ShieldCheck },
  QC_REJECTED:  { label: 'QC Rejected',   color: 'text-red-400',    bg: 'bg-red-500/10',    Icon: ShieldX },
  RACK_SLOTTED: { label: 'Rack Slotted',  color: 'text-amber-400',  bg: 'bg-amber-500/10',  Icon: MapPin },
  ITEM_PICKED:  { label: 'Item Picked',   color: 'text-cyan-400',   bg: 'bg-cyan-500/10',   Icon: ClipboardList },
  KIT_COMPLETED:{ label: 'Kit Completed', color: 'text-primary',    bg: 'bg-primary/10',    Icon: Layers },
  DISPATCH:     { label: 'Dispatch',      color: 'text-orange-400', bg: 'bg-orange-500/10', Icon: TruckIcon },
  all:          { label: 'All',           color: 'text-muted-foreground', bg: 'bg-muted', Icon: ClipboardList },
};

const PAGE_SIZE = 10;

const DEFAULT_FILTERS: AuditFilters = {
  operator: 'All Operators',
  dateFrom: '',
  dateTo: '',
  actionType: 'all',
  search: '',
};

function formatTimestamp(ts: string) {
  const d = new Date(ts.replace('09-11', '09:11')); // fix typo in mock
  const date = d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  const time = d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
  return { date, time };
}

export default function AuditTrailTable() {
  const [filters, setFilters] = useState<AuditFilters>(DEFAULT_FILTERS);
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    return MOCK_LOGS.filter((log) => {
      if (filters.operator !== 'All Operators' && log.operator !== filters.operator) return false;
      if (filters.actionType !== 'all' && log.action !== filters.actionType) return false;
      if (filters.dateFrom) {
        const logDate = log.timestamp.slice(0, 10);
        if (logDate < filters.dateFrom) return false;
      }
      if (filters.dateTo) {
        const logDate = log.timestamp.slice(0, 10);
        if (logDate > filters.dateTo) return false;
      }
      if (filters.search) {
        const q = filters.search.toLowerCase();
        const haystack = [log.item, log.location, log.grnId, log.batchNo, log.operator, log.device].join(' ').toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    }).sort((a, b) => b.timestamp.localeCompare(a.timestamp));
  }, [filters]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleFilterChange = (f: AuditFilters) => {
    setFilters(f);
    setPage(1);
  };

  const handleReset = () => {
    setFilters(DEFAULT_FILTERS);
    setPage(1);
  };

  return (
    <div className="flex flex-col gap-3 flex-1 min-h-0">
      <AuditTrailFiltersBar filters={filters} onChange={handleFilterChange} onReset={handleReset} />

      {/* Stats strip */}
      <div className="flex items-center gap-4 px-1">
        <span className="text-xs text-muted-foreground">
          Showing <span className="font-semibold text-foreground">{filtered.length}</span> log{filtered.length !== 1 ? 's' : ''}
        </span>
        {filtered.length !== MOCK_LOGS.length && (
          <span className="text-xs text-primary font-medium">Filtered from {MOCK_LOGS.length} total</span>
        )}
      </div>

      {/* Table */}
      <div className="flex-1 bg-card border border-border rounded-xl overflow-auto">
        <table className="w-full text-xs min-w-[900px]">
          <thead>
            <tr className="border-b border-border bg-muted/40">
              <th className="text-left px-4 py-3 font-semibold text-muted-foreground uppercase tracking-wider text-2xs w-36">Timestamp</th>
              <th className="text-left px-4 py-3 font-semibold text-muted-foreground uppercase tracking-wider text-2xs">Action</th>
              <th className="text-left px-4 py-3 font-semibold text-muted-foreground uppercase tracking-wider text-2xs">Operator</th>
              <th className="text-left px-4 py-3 font-semibold text-muted-foreground uppercase tracking-wider text-2xs">Device</th>
              <th className="text-left px-4 py-3 font-semibold text-muted-foreground uppercase tracking-wider text-2xs">Item</th>
              <th className="text-left px-4 py-3 font-semibold text-muted-foreground uppercase tracking-wider text-2xs w-24">Qty</th>
              <th className="text-left px-4 py-3 font-semibold text-muted-foreground uppercase tracking-wider text-2xs">Location</th>
              <th className="text-left px-4 py-3 font-semibold text-muted-foreground uppercase tracking-wider text-2xs">Ref</th>
            </tr>
          </thead>
          <tbody>
            {paginated.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-center py-16 text-muted-foreground text-sm">
                  No audit logs match the current filters.
                </td>
              </tr>
            ) : (
              paginated.map((log, idx) => {
                const cfg = ACTION_CONFIG[log.action] ?? ACTION_CONFIG['all'];
                const ActionIcon = cfg.Icon;
                const { date, time } = formatTimestamp(log.timestamp);
                return (
                  <tr
                    key={log.id}
                    className={`border-b border-border/50 transition-colors hover:bg-muted/30 ${idx % 2 === 0 ? '' : 'bg-muted/10'}`}
                  >
                    {/* Timestamp */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="font-mono text-foreground font-medium">{time}</div>
                      <div className="text-muted-foreground text-2xs">{date}</div>
                    </td>

                    {/* Action */}
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-2xs font-semibold ${cfg.bg} ${cfg.color}`}>
                        <ActionIcon size={11} />
                        {cfg.label}
                      </span>
                    </td>

                    {/* Operator */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                          <span className="text-2xs font-bold text-primary">{log.operator.charAt(0)}</span>
                        </div>
                        <span className="text-foreground font-medium truncate max-w-[110px]">{log.operator}</span>
                      </div>
                    </td>

                    {/* Device */}
                    <td className="px-4 py-3">
                      <span className="px-2 py-0.5 rounded bg-muted text-muted-foreground font-mono text-2xs">{log.device}</span>
                    </td>

                    {/* Item */}
                    <td className="px-4 py-3">
                      <span className="text-foreground truncate max-w-[180px] block">{log.item}</span>
                      {log.batchNo && (
                        <span className="text-muted-foreground text-2xs">Batch: {log.batchNo}</span>
                      )}
                    </td>

                    {/* Qty */}
                    <td className="px-4 py-3 text-foreground font-medium">{log.qty}</td>

                    {/* Location */}
                    <td className="px-4 py-3">
                      <span className="text-foreground">{log.location}</span>
                    </td>

                    {/* Ref */}
                    <td className="px-4 py-3">
                      {log.grnId ? (
                        <span className="text-primary text-2xs font-mono">{log.grnId}</span>
                      ) : (
                        <span className="text-muted-foreground text-2xs">—</span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-1">
          <span className="text-xs text-muted-foreground">
            Page {page} of {totalPages}
          </span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-1.5 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft size={14} />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                onClick={() => setPage(p)}
                className={`w-7 h-7 rounded-lg text-xs font-medium transition-colors ${
                  p === page
                    ? 'bg-primary text-primary-foreground'
                    : 'border border-border text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                {p}
              </button>
            ))}
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-1.5 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
